/**
 * ゲームバランス・挙動の一元管理ファイル。
 *
 * ここの数値だけを触ればゲーム感触を調整できる。
 * コンテンツ（料理名など）は src/game/config/stages/ 側で管理する。
 */

import type { SpawnWeight } from './types';

/** アプリ全体の設定。 */
export const APP_CONFIG = {
  /** 起動時に読み込むステージID。 */
  defaultStageId: 'fukuoka-food',
  /** ステージ定義が読めなかった場合のフォールバックタイトル。 */
  fallbackTitle: 'ご当地めしマージ',
  /**
   * localStorage のキーの接頭辞。
   *
   * 県名を含めない。47都道府県・ご当地ステージで同じ接頭辞を使い、
   * ステージ別の記録はこの下にステージIDで分ける。
   */
  storagePrefix: 'meshi-merge',
  /** 実画像の存在確認を行うか（false にすると常に仮アイコン）。 */
  probeImages: true,
} as const;

/**
 * 旧バージョンの保存キー。**移行元としてのみ参照する。**
 *
 * 福岡単独だった頃は、すべての記録が1つのキーに入っていた。
 * 県を増やすとハイスコアも図鑑も混ざるため分割したが、
 * 記事流入で獲得したユーザーの記録を消さないよう、初回起動時に移行する。
 */
export const LEGACY_STORAGE = {
  /** 旧セーブデータのキー。 */
  save: 'fukuoka-meshi-merge:v1',
  /** 旧スコア履歴のキー。 */
  scoreHistory: 'fukuoka-meshi-merge:score-history',
  /** 旧データが属していたステージ。このステージを開いたときだけ移行する。 */
  stageId: 'fukuoka-food',
} as const;

/** ステージ別の記録の保存キー（ハイスコア・図鑑・ツアー時間など）。 */
export function stageStorageKey(stageId: string): string {
  return `${APP_CONFIG.storagePrefix}:stage:${stageId}:v1`;
}

/**
 * 設定の保存キー。
 *
 * 音量や表示の設定は**ステージをまたいで共有する**。
 * 県を移るたびに音量を設定し直すのはおかしいため、記録とは別のキーに置く。
 */
export function settingsStorageKey(): string {
  return `${APP_CONFIG.storagePrefix}:settings:v1`;
}

/**
 * ステージをまたぐ記録（トロフィー・アイテムの所持数・購入状態）の保存キー。
 * 県別の記録とは別の場所に置く。
 */
export function metaStorageKey(
  name: 'trophies' | 'wallet' | 'last-stage' | 'daily' | 'ranking' | 'app-prompts' | 'telemetry' | 'visited',
): string {
  return `${APP_CONFIG.storagePrefix}:${name}:v1`;
}

/** ステージ別のスコア履歴の保存キー。 */
export function scoreHistoryStorageKey(stageId: string): string {
  return `${APP_CONFIG.storagePrefix}:stage:${stageId}:score-history`;
}

/**
 * 料理の半径（レベル1〜12）。**全県共通の正本はここ**。
 *
 * 各県の `*Food.ts` にも同じ値が書いてあり、実際に効くのはそちら。
 * ここは「正しい値はこれ」という基準で、`tests/stageRegistry.test.ts` が
 * 全県をこの表と突き合わせる。**変えるときは必ず両方そろえる。**
 *
 * 2026-09-22 (1): 器を広げたうえで全体を5%縮めた（101→96 ほか）。
 * Lv1〜11を1つずつ抱えた状態が器に収まらず、Lv12が構造的に作れなかったため。
 *
 * 2026-09-22 (2): **Lv7以上だけ伸びを緩めた**（比率1.11）。Lv1〜6は据え置き。
 * 収まるようになっても、抱えたあとに器がもつのが14投下しかなく、
 * 2つめのLv11を作るのに要る53投下ぶんの材料に届かなかった。
 * 階段の面積の大半は上位段が占めるので、そこだけ削るのがいちばん効率がいい。
 * 階段の面積 87,123 → 69,746px²、余力 8,445 → 33,924px²（もつ投下数は平均14→55）。
 * 絵が小さくて読みにくいのは下の段なので、Lv1〜6には触らない。
 */
export const ITEM_RADII = [17, 21, 26, 30, 36, 42, 46, 51, 57, 63, 70, 78] as const;

/** ゲームバランス設定。 */
export const GAME_TUNING = {
  /**
   * 論理解像度。Phaser.Scale.FIT で拡縮するため、
   * どの端末でも半径・重力の調整値が同じ意味になる。
   */
  world: {
    width: 480,
    height: 720,

    /**
     * プレイエリアは長方形の箱ではなく「器（丼）」の形。
     * 内側の輪郭は下半分の楕円で、口から上は開いている。
     * 料理が口からあふれる／器の外へこぼれるとゲームオーバー。
     */
    bowl: {
      /** 器の中心 X。 */
      centerX: 240,
      /** 器の口（縁）の Y。ここが「あふれライン」になる。 */
      rimY: 424,
      /**
       * 口の内側の半幅。
       *
       * 論理解像度 480 の制約で、`rimHalf + lipOut + 陶器の厚み(14)` が画面端に当たる。
       * lipOut を 8 まで絞ったので 210 まで取れる（外周 x 11〜469・左右の余白11px）。
       */
      rimHalf: 210,
      /**
       * 口から底の内側までの深さ。浅くするほど難しくなる。
       *
       * `rimY` は 424 から動かさないこと。背景の豆知識マーカーは「器の縁より上」しか
       * 押せない仕様（tour.ts の isHotspotVisible）で、29県1001個のマーカーがこの位置を前提に
       * 置かれている。縁を 6px 上げると9個、54px 上げると67個が永久に押せなくなる。
       *
       * したがって器を広げる余地は深さ側にしかない。下限は器の影と高台の描画で、
       * `rimY + depth + 41 <= 720`。424 + 252 + 41 = 717。
       */
      depth: 252,
      /**
       * 底の平らさ（スーパー楕円の指数）。2 でただの半楕円。
       *
       * 2 のままだと、器の下3分の1（深さ171px より下）は**最大級の料理が1個しか入らない
       * ポケット**になる。最初にできた Lv11 がそこに沈んで埋まり、2つめは山の上で生まれるので、
       * あいだに小さい段を挟んだまま届かない。実測でも2つの Lv11 は126〜165px離れたまま固まっていた。
       *
       * 2.5 にすると2つ並べる深さが 171 → 195px（器の68% → 77%）になり、内側の面積も
       * 82,952 → 89,277px² に増える。
       *
       * **平らにしすぎると逆効果。** 3.5 や 4 では成績が落ちた。
       * 少し傾斜が残っているほうが、2つの大玉を中央へ寄せてくれる。
       * 「2つ並ぶ幅はあるが、まだ寄せる力は残っている」形がいちばん良い。
       */
      flatness: 2.5,
      /** 内壁を近似する線分の数（多いほど滑らかだがボディが増える）。 */
      segments: 28,
      /**
       * 外へ開く縁（リップ）の張り出しと立ち上がり。ここを越えるとこぼれる。
       *
       * **口より上の壁はここだけ。** 20/12 のころは外へ59度開いた漏斗で、
       * 押しつけられた料理を外へ滑らせていた。8/48 にすると外へ9度の
       * ほぼ垂直な壁が48px立ち、口の上にもう一段ぶん積めるようになる。
       * 壁で囲まれた面積は 84,208 → 103,671px²。
       *
       * 上げすぎると投下レーンを塞ぐ。`(rimY - lipUp) - (spawnY + 最大半径) > 8` を保つこと。
       * 424 - 48 - (262 + 78) = 36px。
       */
      lipOut: 8,
      lipUp: 48,
      /** 陶器の厚み（見た目）。 */
      shell: 14,
      /** 壁ボディの厚み（すり抜け防止のため厚く取る）。 */
      wallThickness: 40,
    },

    /** 料理を生成する Y 座標（器の上の投下レーン）。 */
    spawnY: 262,
    /** 器の外側と判定するまでの余白（誤検知防止）。 */
    spillMarginPx: 14,
    /** この Y より下へ落ちたら「こぼれた」と確定する。 */
    fallOutY: 780,
  },

  physics: {
    gravityY: 1.05,
    /** 反発係数。上げると跳ねすぎるので低めに。 */
    restitution: 0.12,
    /**
     * 摩擦は低め。器はすり鉢状なので、摩擦が高いと料理が斜面の途中で
     * 止まったまま眠ってしまい、「隙間が空いているのに合体しない」
     * 不可解な見た目になる。低くして必ず中心へ寄せ集める。
     */
    friction: 0.16,
    frictionStatic: 0.2,
    /** 空気抵抗。微振動の抑制に効く。 */
    frictionAir: 0.014,
    /** 壁の摩擦（張り付き防止のため低め）。 */
    wallFriction: 0.015,
    wallRestitution: 0.05,
    /** すり抜け防止のための速度上限。 */
    maxVelocity: 24,
    maxAngularVelocity: 0.5,
    /**
     * 輪郭付き（丸くない）料理の最大傾き（ラジアン）。
     * 斜め上から描いた器が逆さまになると不自然なので、この角度までしか傾かない。
     * null にすると自由に回転する。
     */
    shapedMaxTiltRad: 0.35 as number | null,
    /** ソルバ精度（すり抜け・めり込み対策）。 */
    positionIterations: 10,
    velocityIterations: 8,
    constraintIterations: 4,
    /** スリープ（永久微振動の抑制）。 */
    enableSleeping: true,
    sleepThreshold: 45,
  },

  drop: {
    /** 投下後の待機時間（ミリ秒）。この間は次を落とせない。 */
    cooldownMs: 430,
    /** 投下時に与える初速。 */
    initialVelocityY: 1.5,
    /** キーボードでの左右移動量（1回あたり）。 */
    keyboardStep: 14,
    /** ガイド線の透明度。 */
    guideAlpha: 0.35,
  },

  merge: {
    /** 合体時のポップ演出の最大倍率。 */
    popScale: 1.22,
    popDurationMs: 170,
    /** 粒子演出の個数。 */
    particleCount: 10,
    /** 1フレームで処理する合体要求の上限（保険）。 */
    maxPerFrame: 8,
    /** これ以上のレベルができたら「名場面」として録画に残す。 */
    highlightMinLevel: 10,
    /** 最高レベル同士が接触したときのボーナス得点。 */
    maxLevelBonus: 2048,
    /** 同一ペアのボーナス連続加算を防ぐクールダウン。 */
    maxLevelBonusCooldownMs: 1200,
    /** collisionstart を取り逃した場合の近接セーフティスキャン間隔。 */
    safetyScanIntervalMs: 300,
    /**
     * セーフティスキャンで「接触」とみなす距離の比率（半径合計 × この値）。
     * わずかに 1 を超えさせて、ほぼ触れている同レベル同士を必ず拾う。
     * （見た目は接しているのに合体しない、という不満をなくすため）
     */
    safetyScanTouchRatio: 1.06,
  },

  /**
   * 見た目の弾力（プルプル）。物理には一切影響せず、スプライトのスケールだけを揺らす。
   * 着地や合体でスカッシュ＆ストレッチが起き、減衰しながら収まっていく。
   */
  jiggle: {
    /** 基準となる揺れの速さ（Hz）。半径が大きい料理ほど自動で遅くなる。 */
    baseFrequencyHz: 6.4,
    /** 揺れが収まるまでの時定数（秒）。大きいほど長く揺れる。 */
    dampingTauSec: 0.26,
    /** 潰れの最大量（0.2 なら縦横 ±20%）。 */
    maxAmplitude: 0.19,
    /** 静止中も残しておく微振動。「生きている」感じを出す。 */
    idleAmplitude: 0.012,
    /** この速度差より大きく減速したフレームを「着地」とみなす。 */
    impactSpeedThreshold: 1.1,
    /** 潰れ量が最大になるまでの速度差の幅。 */
    impactSpeedRange: 7,
  },

  score: {
    /**
     * 'formula' : score = base ^ 生成レベル（例: レベル3生成 → 8点）
     * 'item'    : ステージ定義の item.score をそのまま使用
     */
    mode: 'formula' as 'formula' | 'item',
    base: 2,
  },

  combo: {
    /** 前回の合体からこの時間内に合体するとコンボ継続。 */
    windowMs: 1500,
    /**
     * コンボ倍率。index 0 が 1コンボ目。
     * 配列長を超えたコンボ数には最後の値が適用される。
     */
    multipliers: [1.0, 1.1, 1.2, 1.3, 1.5],
  },

  gameOver: {
    /**
     * ゲームオーバーは「器の外へこぼれたとき」だけ。
     * 口より上に積み上がっただけでは終了せず、下の警告表示に使う。
     */
    warningMs: 600,
    /**
     * 「こぼれそう！」を出すのは、口より上にあって、かつ器の中心から
     * 口の半幅のこの割合より外側にはみ出している料理があるときだけ。
     */
    edgeRatio: 0.72,
    /** こぼれた料理を赤く見せてから結果画面を出すまでの時間。 */
    revealSpillMs: 1100,
    /** 生成直後の料理を警告対象から外す猶予時間。 */
    ignoreNewPieceMs: 700,
    /** 警告対象にする最小のはみ出し量（誤検知防止）。 */
    lineMarginPx: 2,
  },

  spawn: {
    /**
     * 抽選テーブル。**ここが全県の唯一の定義**。
     *
     * ステージ側で `spawnWeights` を書けば上書きできるが、どの県も書いていない。
     * 県ごとに変えるものではないので、新しい県のデータにも書かないこと。
     *
     * 1投下あたりの平均価値で1プレイの長さが決まる。これがレベル12到達の主因。
     *
     *   40/30/20/10（〜2026-09-22）  平均 2.6（レベル1換算） → 最低788投下・約5.6分
     *   レベル1〜5 均等              平均 6.2              → 最低330投下・約2.4分
     *   **いまの 10/15/20/25/20/10   平均 9.6              → 最低213投下・約1.5分**
     *   （参考）スイカゲーム                                → 最低165投下
     *
     * レベル6まで出すのはスイカゲームと同じ構造（11段中5段目まで＝ちょうど半分）。
     * レベル6は直径84pxで口の21%。スイカゲームのデコポンとほぼ同じ比率になる。
     *
     * **レベル1を外さないこと。** 12品を見せるのがこのゲームの目的なので、
     * 最下段の料理が一度も画面に出ないのは本末転倒になる。
     */
    defaultWeights: [
      { level: 1, weight: 10 },
      { level: 2, weight: 15 },
      { level: 3, weight: 20 },
      { level: 4, weight: 25 },
      { level: 5, weight: 20 },
      { level: 6, weight: 10 },
    ] as SpawnWeight[],
    /** 重み合計の期待値。ずれた場合は検証で警告する。 */
    expectedTotal: 100,
    /** 許容誤差。 */
    totalTolerance: 0.001,
  },

  effects: {
    /** 画面振動の強さ・長さ。 */
    shakeDurationMs: 140,
    shakeIntensity: 0.004,
    /** スコアポップの表示時間。 */
    scorePopDurationMs: 620,
    /** ぶつかったときの小さな波紋を出す強さの下限（0〜1。弱い接触では出さない）。 */
    impactFxMinStrength: 0.35,
    /** 波紋を出す間隔の下限（積み上がった山で連発しないように）。 */
    impactFxCooldownMs: 70,
    /** これ以上のレベルができたら、名前の帯を出す（初めて作った料理はレベルに関係なく出す）。 */
    bannerMinLevel: 9,
  },

  /**
   * 情景演出（背景の屋台・夜景）。
   * 「福岡に遊びに来ている感覚」を出すための見た目パラメータ。
   */
  scenery: {
    /** 湯気を立てる最小レベル（高レベルほど「あつあつ」に見せる）。 */
    steamMinLevel: 7,
    /**
     * 到達レベルに応じて情景が進む境目。
     * 夕暮れ → 薄暮 → 夜 → 深夜 → 夜明け の 5 段階。
     * 「遊ぶほど景色が変わる」ことをご褒美にして離脱を防ぐ。
     */
    phase: { evening: 4, night: 6, deepnight: 8, dawn: 10 },
    /** 情景クロスフェードの時間。 */
    phaseFadeMs: 900,
    /** パララックスの揺れ幅（遠景 / 中景）と周期。 */
    parallaxFarPx: 7,
    parallaxNearPx: 16,
    parallaxDurationMs: 21000,
    /**
     * ケン・バーンズ効果（背景をごくゆっくり寄せながら流す）。
     * 静止した絵でも「その場所にいる」感じが出るので、写真背景でも効果が大きい。
     * 端が見切れないよう minScale は必ず 1 より大きくすること。
     */
    kenBurns: {
      minScale: 1.06,
      maxScale: 1.16,
      driftXPx: 11,
      driftYPx: 9,
      durationMs: 38000,
    },
    /** 花火が上がりはじめる時間帯（夜以降）と打ち上げ間隔。 */
    fireworksFromPhase: 'night',
    fireworksIntervalMs: 5200,
    /** 流れ星が流れはじめる時間帯（深夜）と間隔。 */
    shootingStarFromPhase: 'deepnight',
    shootingStarIntervalMs: 7600,
    /** 屋形船が湾を横切る周期。 */
    boatDurationMs: 42000,
    /**
     * 背景ツアー「福岡めぐり」の設定。
     * 写真が配置されている場合、時間経過で景色が切り替わる。
     */
    tour: {
      /** 1景あたりの表示時間（ミリ秒）。 */
      secondsPerScene: 60,
      /** 最後の景色まで進んだら最初に戻るか。 */
      loop: true,
      /** 景色切り替えのクロスフェード時間（ミリ秒）。 */
      crossfadeMs: 1500,
      /** 看板の表示時間（ミリ秒）。 */
      signboardMs: 2000,
      /** ホットスポットマーカーの当たり判定半径（論理ピクセル）。 */
      hotspotHitRadius: 22,
      /**
       * マーカーを器の縁からこれだけ上に留める余白。
       *
       * 縁より下のマーカーは器の内側ごしに光って見えるのに押せない、という状態になる。
       * `tourHotspotWorldPos` がここまで引き上げる。当たり判定の半径22pxぶんを
       * 縁から離すため 30 にしてある。
       */
      hotspotRimMarginPx: 30,
      /** 背景画像を表示している間、屋台の飾りを非表示にする。 */
      showYataiDecor: false,
    },
  },
} as const;

/** 設定の既定値（localStorage が空のときに使う）。 */
export const DEFAULT_SETTINGS = {
  bgmVolume: 0.3,
  seVolume: 0.7,
  showGuide: true,
  showShake: true,
  showEvolutionList: true,
  /** 文字を大きくする（ふだんゲームをしない人・年配の人向け）。 */
  largeText: false,
  /** Lv10以上ができた瞬間を短い動画に残す（端末の負荷があるので既定は切）。 */
  recordHighlights: false,
  /** 器のスキン。 */
  bowlSkin: 'yatai' as string,
  /**
   * 動きを減らす（光・輪・画面の揺れ・最上位のカットインの後光を止める）。
   * 端末の「視差効果を減らす」が入っていれば、この設定が切でも減らす。
   */
  reduceMotion: false,
  /** 遊び方の記録を送る（個人は特定しない。src/meta/telemetry.ts）。既定は入。 */
  sendUsageStats: true,
  /** 配信モード：合間の広告とお知らせ（トースト・吹き出し）を止め、スコアを大きく出す。 */
  streamerMode: false,
} as const;

export type GameSettings = {
  bgmVolume: number;
  seVolume: number;
  showGuide: boolean;
  showShake: boolean;
  showEvolutionList: boolean;
  largeText: boolean;
  recordHighlights: boolean;
  bowlSkin: string;
  reduceMotion: boolean;
  sendUsageStats: boolean;
  streamerMode: boolean;
};
